import { useState, type FormEvent } from 'react';
import { toErrorMessage } from '../api/client';
import type { ClientAccessView } from '../api/types';
import { useToast } from './ToastProvider';

/**
 * Face-match pass lines, one per model: their scores are on different scales, so one number cannot
 * serve both. Bounds mirror Core FaceMatchThresholds, which enforces them.
 * - SFace: cosine similarity, shown as 0.000. What verify-app decides with.
 * - face-api: 1 - distance/1.5, shown as a percentage. verify-app's fallback when SFace can't load.
 */
export const FACE_MATCH_BOUNDS = {
  sface: { floor: 0.2, ceiling: 0.9 },
  neural: { floor: 0.5, ceiling: 0.95 },
} as const;

const roundSFace = (v: number) => Math.round(v * 1000) / 1000;
const roundNeural = (v: number) => Math.round(v * 100) / 100;

/** The two inputs, shared by Platform Settings (minimums) and Verification Rules (client lines). */
export function FaceMatchThresholdInputs({
  idPrefix,
  sface,
  neural,
  onChange,
  sfaceMin,
  neuralMin,
  disabled,
  sfaceHelp,
  neuralHelp,
}: {
  idPrefix: string;
  sface: number;
  neural: number;
  onChange: (sface: number, neural: number) => void;
  sfaceMin: number;
  neuralMin: number;
  disabled?: boolean;
  sfaceHelp?: string;
  neuralHelp?: string;
}) {
  return (
    <div className="row g-3">
      <div className="col-md-6">
        <label htmlFor={`${idPrefix}SFace`} className="form-label small fw-semibold">
          SFace (primary model)
        </label>
        <div className="input-group">
          <input
            id={`${idPrefix}SFace`}
            className="form-control"
            type="number"
            step="0.001"
            min={sfaceMin}
            max={FACE_MATCH_BOUNDS.sface.ceiling}
            required
            disabled={disabled}
            value={sface}
            onChange={(e) => onChange(Number(e.target.value), neural)}
          />
          <span className="input-group-text">similarity</span>
        </div>
        {sfaceHelp && <div className="form-text">{sfaceHelp}</div>}
      </div>
      <div className="col-md-6">
        <label htmlFor={`${idPrefix}Neural`} className="form-label small fw-semibold">
          face-api (fallback model)
        </label>
        <div className="input-group">
          <input
            id={`${idPrefix}Neural`}
            className="form-control"
            type="number"
            step="1"
            min={Math.round(neuralMin * 100)}
            max={Math.round(FACE_MATCH_BOUNDS.neural.ceiling * 100)}
            required
            disabled={disabled}
            value={Math.round(neural * 100)}
            onChange={(e) => onChange(sface, Number(e.target.value) / 100)}
          />
          <span className="input-group-text">%</span>
        </div>
        {neuralHelp && <div className="form-text">{neuralHelp}</div>}
      </div>
    </div>
  );
}

/**
 * Settings → Verification Rules (and the client page's Verification Rules tab): the client's own
 * face-match lines. They can only be raised: the inputs stop at the platform minimum and the
 * server refuses anything below it. A line left at the platform minimum is saved as "follow the
 * platform", so it moves when the platform minimum does.
 */
export function FaceMatchThresholdCard({
  access,
  canManage,
  onSave,
  className = 'mb-4',
}: {
  access: ClientAccessView;
  canManage: boolean;
  onSave: (sface: number | null, neural: number | null) => Promise<string | null | undefined>;
  className?: string;
}) {
  const { showSuccess, showError } = useToast();
  const [sface, setSFace] = useState(access.faceMatchSFace);
  const [neural, setNeural] = useState(access.faceMatchNeural);
  const [busy, setBusy] = useState(false);

  const platformSFace = access.platformFaceMatchSFace;
  const platformNeural = access.platformFaceMatchNeural;
  const usingPlatform = access.faceMatchSFaceOverride === null && access.faceMatchNeuralOverride === null;

  async function save(nextSFace: number | null, nextNeural: number | null) {
    setBusy(true);
    try {
      showSuccess((await onSave(nextSFace, nextNeural)) ?? 'Saved.');
      if (nextSFace === null) setSFace(platformSFace);
      if (nextNeural === null) setNeural(platformNeural);
    } catch (err) {
      showError(toErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const s = roundSFace(sface);
    const n = roundNeural(neural);
    if (s < platformSFace || n < platformNeural) {
      showError('A face match threshold cannot be below the platform minimum. It can only be raised.');
      return;
    }
    void save(s === platformSFace ? null : s, n === platformNeural ? null : n);
  }

  return (
    <div className={`card ${className}`}>
      <div className="card-header">
        <h6 className="mb-0">
          <i className="bi bi-person-bounding-box me-2 text-primary" />
          Face match threshold
        </h6>
      </div>
      <form onSubmit={handleSubmit}>
        <div className="card-body">
          <p className="small text-body-secondary">
            A selfie that scores below the line against the ID photo is declined. You can make face match
            stricter than the platform minimum, but not looser. Higher lines decline more look-alikes, and
            more genuine customers in poor light.
          </p>
          <FaceMatchThresholdInputs
            idPrefix="clientFaceMatch"
            sface={sface}
            neural={neural}
            onChange={(s, n) => {
              setSFace(s);
              setNeural(n);
            }}
            sfaceMin={platformSFace}
            neuralMin={platformNeural}
            disabled={!canManage}
            sfaceHelp={`Platform minimum ${platformSFace.toFixed(3)}.`}
            neuralHelp={`Platform minimum ${Math.round(platformNeural * 100)}%.`}
          />
          <div className="form-text mt-2">
            {usingPlatform
              ? 'Using the platform minimum.'
              : 'Lines at the platform minimum follow it if it changes; a raised platform minimum always applies.'}
          </div>
        </div>
        {canManage && (
          <div className="card-footer bg-transparent d-flex justify-content-end gap-2">
            {!usingPlatform && (
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                disabled={busy}
                onClick={() => void save(null, null)}
              >
                Use platform minimum
              </button>
            )}
            <button type="submit" className="btn btn-sm btn-primary" disabled={busy}>
              Save face match threshold
            </button>
          </div>
        )}
      </form>
    </div>
  );
}
